import { Home, Plus, Eye, ShieldCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "wouter";

export default function ApartmentMarketplaceSection() {
  return (
    <section id="apartment-marketplace" className="py-16 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-800">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4">
            <Home className="w-6 h-6 text-blue-600" />
            <h2 className="text-4xl font-bold text-slate-900 dark:text-white">
              Apartment Marketplace
            </h2>
          </div>
          <p className="text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
            Buy, sell, or rent apartments within the Purva Swanlake community. Connect directly with residents and find your next home.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {/* Browse Listings */}
          <Link href="/apartments">
            <Card className="p-6 text-center hover:shadow-lg transition-all hover:scale-105 cursor-pointer group h-full">
              <Eye className="w-12 h-12 text-blue-600 mx-auto mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                Browse Listings
              </h3>
              <p className="text-slate-600 dark:text-slate-300 text-sm mb-4">
                Explore available apartments with advanced search filters
              </p>
              <span className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium text-sm">
                View Marketplace <ArrowRight className="w-4 h-4" />
              </span>
            </Card>
          </Link>

          {/* Post Your Apartment */}
          <Link href="/post-apartment">
            <Card className="p-6 text-center hover:shadow-lg transition-all hover:scale-105 cursor-pointer group h-full">
              <Plus className="w-12 h-12 text-green-600 mx-auto mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                Post Your Apartment
              </h3>
              <p className="text-slate-600 dark:text-slate-300 text-sm mb-4">
                List your apartment for rent or sale to the community
              </p>
              <span className="inline-flex items-center gap-1 text-green-600 hover:text-green-700 font-medium text-sm">
                Create Listing <ArrowRight className="w-4 h-4" />
              </span>
            </Card>
          </Link>

          {/* Manage Listings */}
          <Link href="/my-apartments">
            <Card className="p-6 text-center hover:shadow-lg transition-all hover:scale-105 cursor-pointer group h-full">
              <ShieldCheck className="w-12 h-12 text-purple-600 mx-auto mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                Manage Listings
              </h3>
              <p className="text-slate-600 dark:text-slate-300 text-sm mb-4">
                Edit, update, or remove your apartment listings
              </p>
              <span className="inline-flex items-center gap-1 text-purple-600 hover:text-purple-700 font-medium text-sm">
                My Listings <ArrowRight className="w-4 h-4" />
              </span>
            </Card>
          </Link>

          {/* Admin Moderation */}
          <Link href="/admin/apartments-moderation">
            <Card className="p-6 text-center hover:shadow-lg transition-all hover:scale-105 cursor-pointer group h-full">
              <Home className="w-12 h-12 text-orange-600 mx-auto mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                Admin Panel
              </h3>
              <p className="text-slate-600 dark:text-slate-300 text-sm mb-4">
                Review and moderate live apartment listings
              </p>
              <span className="inline-flex items-center gap-1 text-orange-600 hover:text-orange-700 font-medium text-sm">
                Moderation Queue <ArrowRight className="w-4 h-4" />
              </span>
            </Card>
          </Link>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Ready to List Your Apartment?</h3>
          <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
            Join our community marketplace and connect with potential buyers or renters. Post your listing for free and reach interested residents instantly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/post-apartment">
              <Button className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-8 py-3 rounded-lg">
                Post Your Apartment Now
              </Button>
            </Link>
            <Link href="/apartments">
              <Button variant="outline" className="border-white text-white hover:bg-blue-500 font-semibold px-8 py-3 rounded-lg bg-transparent">
                Browse Listings
              </Button>
            </Link>
          </div>
        </div>

        {/* Key Features */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600 mb-2">100% Free</div>
            <p className="text-slate-600 dark:text-slate-300">No listing fees or hidden charges</p>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600 mb-2">Auto-Published</div>
            <p className="text-slate-600 dark:text-slate-300">Listings go live immediately after posting</p>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600 mb-2">Community First</div>
            <p className="text-slate-600 dark:text-slate-300">Connect directly with residents</p>
          </div>
        </div>
      </div>
    </section>
  );
}
